import React, { useEffect, useState, useCallback } from "react";
import { Mail, Loader2, Tag, RefreshCw, Unplug, CheckCircle2 } from "lucide-react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";

/**
 * Gmail Watch — operator-facing page for the Gmail ingestor (iter 31.13).
 *
 * Mirror of DriveWatch.jsx, scoped to a single Gmail label. Operator
 * creates a label in Gmail (e.g. "_Shed"), applies it to anything they
 * want filed, picks the label here, and the Shed polls every 5 min.
 */
export default function GmailWatch() {
    const [status, setStatus] = useState(null);
    const [labels, setLabels] = useState(null);
    const [busy, setBusy] = useState(false);
    const [polling, setPolling] = useState(false);
    const [err, setErr] = useState("");

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/oauth/gmail/status");
            setStatus(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    }, []);

    const loadLabels = useCallback(async () => {
        if (!status?.connected) return;
        try {
            const { data } = await api.get("/oauth/gmail/labels");
            setLabels(data.labels || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    }, [status?.connected]);

    useEffect(() => { loadStatus(); }, [loadStatus]);
    useEffect(() => { loadLabels(); }, [loadLabels]);

    // Detect ?connected=1 / ?error=... in the URL on first mount and clean it.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("connected") === "1") {
            window.history.replaceState({}, "", "/gmail-watch");
            loadStatus();
        } else if (params.get("error")) {
            setErr(`Gmail connect failed: ${params.get("error")}`);
            window.history.replaceState({}, "", "/gmail-watch");
        }
    }, [loadStatus]);

    const connect = async () => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/oauth/gmail/connect");
            // Iter 31.21: prefer the new `url` key, fall back to legacy
            // `auth_url` so the page works on both old and new backend.
            window.location.href = data.url || data.auth_url;
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
            setBusy(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Gmail watch? Filed emails stay in the Ledger.")) return;
        setBusy(true); setErr("");
        try {
            await api.delete("/oauth/gmail/disconnect");
            setStatus(null);
            setLabels(null);
            await loadStatus();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const pickLabel = async (label) => {
        setBusy(true); setErr("");
        try {
            await api.post("/oauth/gmail/label", {
                label_id: label.id,
                label_name: label.name,
            });
            await loadStatus();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const pollNow = async () => {
        setPolling(true); setErr("");
        try {
            await api.post("/oauth/gmail/poll");
            // Poll is fire-and-forget — wait a few seconds, then refresh status.
            setTimeout(loadStatus, 3500);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setTimeout(() => setPolling(false), 3500);
        }
    };

    return (
        <Shell>
            <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="gmail-watch-page">
                <div className="paper-card p-6">
                    <div className="label-typed text-stamp-red">Ingestor · Gmail</div>
                    <h1 className="font-heading text-3xl md:text-4xl text-ink mt-1">Gmail Watch</h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Connect Gmail. Pick a <strong>label</strong> (e.g. <span className="text-stamp-red">_Shed</span>).
                        Apply that label in Gmail to anything you want filed — the Shed polls
                        every 5 minutes and files matching messages as <strong>comm</strong> entries
                        in the Ledger. Read-only access. No replies, no marking-as-read, no inbox spam.
                    </p>
                    {err && (
                        <div className="mt-4 px-3 py-2 border border-stamp-red/40 bg-stamp-red/5 text-stamp-red text-sm" data-testid="gmail-error">
                            {err}
                        </div>
                    )}
                </div>

                {!status?.configured && (
                    <div className="paper-card p-5 border border-stamp-red/30" data-testid="gmail-not-configured">
                        <div className="label-typed text-stamp-red mb-1">Not configured</div>
                        <p className="font-body text-sm text-ink-soft">
                            Backend is missing <code>GOOGLE_CLIENT_ID</code> / <code>GOOGLE_CLIENT_SECRET</code>.
                            Same env vars as Drive Watch — if Drive works, this works.
                        </p>
                    </div>
                )}

                {status?.configured && !status?.connected && (
                    <div className="paper-card p-5" data-testid="gmail-disconnected">
                        <div className="label-typed text-ink-soft mb-1">Not connected</div>
                        <p className="font-body text-sm text-ink mb-4">
                            Authorise Gmail read-only access. Same Google account you use for Drive is fine,
                            but you can use a different one.
                        </p>
                        <button
                            onClick={connect}
                            disabled={busy}
                            data-testid="gmail-connect-btn"
                            className="inline-flex items-center gap-2 px-5 py-3 bg-stamp-red text-paper-light font-heading uppercase tracking-[0.22em] text-xs hover:bg-stamp-red/90 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            Connect Gmail
                        </button>
                    </div>
                )}

                {status?.connected && !status?.watched_label_id && labels && (
                    <div className="paper-card p-5" data-testid="gmail-pick-label">
                        <div className="label-typed text-stamp-red mb-1">Pick a label</div>
                        <p className="font-body text-sm text-ink-soft mb-4">
                            Connected as <strong className="text-ink">{status.google_email}</strong>.
                            Pick the label whose messages the Shed should watch.
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {labels.map((l) => (
                                <li key={l.id}>
                                    <button
                                        onClick={() => pickLabel(l)}
                                        disabled={busy}
                                        data-testid={`gmail-label-${l.id}`}
                                        className="w-full text-left border border-ink/25 px-3 py-2 hover:border-stamp-red hover:text-stamp-red flex items-center justify-between"
                                    >
                                        <span className="font-heading text-sm">
                                            <Tag className="w-3 h-3 inline mr-1.5" />
                                            {l.name}
                                        </span>
                                        {l.type === "system" && (
                                            <span className="label-typed text-[9px] text-ink-soft">system</span>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {labels.length === 0 && (
                            <div className="font-body text-sm text-ink-soft">
                                No user labels found. Create one in Gmail first (e.g. <code>_Shed</code>),
                                apply it to a few emails, then come back and refresh.
                            </div>
                        )}
                        <div className="mt-4">
                            <button
                                onClick={disconnect}
                                className="text-xs font-heading uppercase tracking-[0.18em] text-ink-soft hover:text-stamp-red"
                                data-testid="gmail-disconnect-link"
                            >
                                Disconnect Gmail
                            </button>
                        </div>
                    </div>
                )}

                {status?.connected && status?.watched_label_id && (
                    <div className="paper-card p-5" data-testid="gmail-watching">
                        <div className="flex items-center gap-2 text-stamp-green">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="label-typed">Watching</span>
                        </div>
                        <div className="font-heading text-2xl text-ink mt-2">
                            Label · <span className="text-stamp-red">{status.watched_label_name}</span>
                        </div>
                        <p className="font-body text-sm text-ink-soft mt-1">
                            {status.google_email} · polled every 5 min ·
                            Last poll: {status.last_poll_at || "never"}
                        </p>

                        <div className="grid grid-cols-2 gap-4 mt-5">
                            <div className="border border-ink/15 p-3">
                                <div className="label-typed text-[10px]">Filed</div>
                                <div className="font-heading text-2xl text-ink">
                                    {status.messages_ingested_total ?? 0}
                                </div>
                            </div>
                            <div className="border border-ink/15 p-3">
                                <div className="label-typed text-[10px]">Skipped (dupes)</div>
                                <div className="font-heading text-2xl text-ink-soft">
                                    {status.messages_duplicates_total ?? 0}
                                </div>
                            </div>
                        </div>

                        {status.last_poll_result && (
                            <div className="mt-3 font-body text-xs text-ink-soft truncate">
                                Last result: <code>{status.last_poll_result}</code>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3 mt-5">
                            <button
                                onClick={pollNow}
                                disabled={polling}
                                data-testid="gmail-poll-btn"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-stamp-red text-paper-light font-heading uppercase tracking-[0.18em] text-[11px] hover:bg-stamp-red/90 disabled:opacity-50"
                            >
                                {polling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                {polling ? "Polling…" : "Poll now"}
                            </button>
                            <button
                                onClick={() => pickLabel({ id: "", name: "" })}
                                disabled={busy}
                                data-testid="gmail-change-label-btn"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-ink/30 font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red"
                            >
                                <Tag className="w-3 h-3" />
                                Change label
                            </button>
                            <button
                                onClick={disconnect}
                                disabled={busy}
                                data-testid="gmail-disconnect-btn"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-ink/30 font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red"
                            >
                                <Unplug className="w-3 h-3" />
                                Disconnect
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Shell>
    );
}
